import * as React from 'react';

import { Trans } from '@lingui/react/macro';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { isPediatricToothId } from '@videa/utils';

import { PreferenceButton } from '../../../common-components/composed/preferenceButton/PreferenceButton';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import {
  blueBlack400,
  blueBlack650,
  blueBlack700,
  white,
} from '../../../theme/videa-style-tokens';
import AnnotatorConstants from '../annotator.constants';
import { VideaBadge } from '../multiFileViewer/findingViewer/imageBrowser/styles';
import type { ImageObject } from '../types';

type ImageFilterSelectorProps = {
  activeImage: {
    objectId: string;
    imageQuality: {
      value: string;
    };
  };
  numberOfSortedFMXImages: number;
  currentImageFilter: string;
  onSetCurrentImageFilter: (imageType: string) => void;
  numberOfBitewings: number;
  onUpdateOdontogramView: (isVisible: boolean) => void;
  sidebarIsNotCollapsed: boolean;
  onExpandSidebar: (isNotCollapsed: boolean) => void;
  sortedAndFilteredImages?: ImageObject[];
};
/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'theme' is defined but never used. */
const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    gap: '0.5em',
    alignItems: 'center',
    fontFamily: 'proxima-nova, sans-serif',
    marginRight: '1em',
  },
  badImageLabel: {
    marginRight: '1em',
    color: 'yellow',
    cursor: 'default',
    fontSize: '.875em',
    fontFamily: 'proxima-nova, sans-serif',
  },
  selectorLabel: {
    border: 'none',
    fontWeight: 'bold',
    height: '2.85em',
    backgroundColor: blueBlack400,
    color: white,
    cursor: 'pointer',
    fontSize: '.875em',
    fontFamily: 'proxima-nova, sans-serif',
    paddingRight: '2.25em',
    '& > svg': {
      verticalAlign: 'text-bottom',
      marginBottom: '2px',
      marginLeft: '6px',
    },
    '& .MuiBadge-colorInfo': {
      backgroundColor: white,
      color: blueBlack700,
    },
  },
  odontogramLabel: {
    border: 'none',
    '& .icon-shape': {
      fill: white,
    },
  },
  inactiveOdontogramLabel: {
    border: 'none',
    '& .icon-shape': {
      fill: blueBlack400,
    },
  },
  inactiveSelectorLabel: {
    border: 'none',
    height: '2.85em',
    paddingRight: '2.25em',
    backgroundColor: blueBlack650,
    fontSize: '.875em',
    fontWeight: '400',
    color: white,
    cursor: 'pointer',
    fontFamily: 'proxima-nova, sans-serif',
    '& .MuiBadge-colorInfo': {
      backgroundColor: blueBlack400,
      color: blueBlack700,
    },
    '& .MuiTypography-labelSmallStrong': {
      fontWeight: '400',
    },
    '& > svg': {
      verticalAlign: 'text-bottom',
      marginBottom: '2px',
      marginLeft: '6px',
    },
  },
  tooltipText: {
    width: '270px',
    padding: '5px',
    color: white,
    display: 'block',
    fontFamily: 'proxima-nova, sans-serif',
  },
  popper: {
    '& .MuiTooltip-tooltip': {
      backgroundColor: 'rgba(34, 34, 34, .9)',
    },
  },
}));

const ImageFilterSelector: React.FC<ImageFilterSelectorProps> = ({
  activeImage,
  numberOfSortedFMXImages,
  currentImageFilter,
  onSetCurrentImageFilter,
  numberOfBitewings,
  onUpdateOdontogramView,
  onExpandSidebar,
  sortedAndFilteredImages,
}) => {
  const classes = useStyles();

  const pediatricToothIds: string[] = [];
  sortedAndFilteredImages?.forEach((image: ImageObject) =>
    image.models?.tooth_identification?.findings.forEach(
      (toothFinding) =>
        isPediatricToothId(String(toothFinding.toothNumber)) &&
        pediatricToothIds.push(String(toothFinding.toothNumber)),
    ),
  );

  return (
    <div className={classes.container}>
      {activeImage?.imageQuality?.value === 'unacceptable' && (
        <Typography variant="subtitle1" className={classes.badImageLabel}>
          <Trans>This image may need to be retaken.</Trans>
        </Typography>
      )}
      <>
        {
          <PreferenceButton
            className={
              currentImageFilter === AnnotatorConstants.FMX
                ? classes.selectorLabel
                : classes.inactiveSelectorLabel
            }
            onClick={() => {
              onSetCurrentImageFilter(AnnotatorConstants.FMX);
              onUpdateOdontogramView(false);
              onExpandSidebar(true);
            }}
          >
            <VideaTypography variant="labelSmallStrong">
              <Trans>FMX</Trans>
              <VideaBadge badgeContent={numberOfSortedFMXImages} color="info" />
            </VideaTypography>
          </PreferenceButton>
        }
        {numberOfBitewings > 0 && (
          <PreferenceButton
            className={
              currentImageFilter === AnnotatorConstants.BITEWING
                ? classes.selectorLabel
                : classes.inactiveSelectorLabel
            }
            onClick={() => {
              onSetCurrentImageFilter(AnnotatorConstants.BITEWING);
              onExpandSidebar(true);
            }}
          >
            <VideaTypography variant="labelSmallStrong">
              <Trans>BW</Trans>
              <VideaBadge badgeContent={numberOfBitewings} color="info" />
            </VideaTypography>
          </PreferenceButton>
        )}
      </>
    </div>
  );
};

export default ImageFilterSelector;
